import { Component, Input, OnInit, Output, EventEmitter } from "@angular/core";
import { Router } from "@angular/router";
import { SideNavBarVisibilityService } from "../services/side-nav-bar-visibility.service";
import { ZpDataService } from "../services/zp-data.service";
import { AdminDashboardService } from "../services/admin-dashboard.service";
import { ChartDataset, ChartType } from "chart.js";
import { EventsRefreshService } from "../services/events-refresh.service";

@Component({
    selector: "app-events-pane",
    templateUrl: "./events-pane.component.html",
    styleUrls: ["./events-pane.component.scss"],
    standalone: false
})
export class EventsPaneComponent implements OnInit {
  public editing;
  @Input() public displayType;
  public _uid;
  @Input() public set uid(id) {
    this._uid = id;
    this.retrieveSettings();
  }
  public get uid(): string {
    return this._uid;
  }
  public dataLoaded;
  public columns;
  public rows;
  public events;
  @Input() public filter = "EVENT(event);EVENT(section);EVENT(created_at)";
  @Input() public limit = 100;
  @Input() public startDate;
  @Input() public endDate;
  @Input() public aggregator = "EACH(COUNT,GROUPBY(EVENTS(all),1))";
  @Input() public sectionFilter = "None";
  @Input() public eventFilter = "None";
  @Input() public title;
  @Input() public subtitle;
  @Output() public deleted = new EventEmitter<string>();
  public pieData;
  public pieLabels;
  public pieChartType: ChartType = "pie";
  public lineChartType: ChartType = "line";
  public barChartType: ChartType = "bar";
  public donutColors = [
    {
      backgroundColor: [
        "#34274D",
        "#471A66",
        "#5C4A7E",
        "#BCB7C8",
        "#EFEEF8",
        "#FFEED6",
        "#EBD4C5",
        "#98ABB6",
      ],
    },
  ];
  public lineChartDataSets: ChartDataset[];
  public uniqueSectionList;
  public uniqueEventList;
  refreshForm: boolean;
  private refreshSubscription;

  constructor(
    private router: Router,
    public sideNavBarVisibilityService: SideNavBarVisibilityService,
    private ZpDataService: ZpDataService,
    private admin: AdminDashboardService,
    private refresher: EventsRefreshService
  ) {}

  ngOnInit(): void {
    if (!this.displayType) {
      this.displayType = "table";
    }
    this.editing = false;
    this.dataLoaded = false;
    this.refreshForm = false;
    this.refreshSubscription = this.refresher.getMessage().subscribe((msg) => {
      let dateUpdate = JSON.parse(msg);
      if (dateUpdate && dateUpdate.startDate) {
        this.startDate = dateUpdate.startDate;
        this.endDate = dateUpdate.endDate;
        this.refresh();
      }
    });
  }
  ngOnDestroy(): void {
    this.refreshSubscription.unsubscribe();
  }

  public async edit() {
    this.editing = true;
    this.refreshForm = false;
    this.dataLoaded = false;
    this.uniqueEventList = await this.admin.retrieveEventParams("event");
    this.uniqueSectionList = await this.admin.retrieveEventParams("section");
  }
  public async refresh() {
    if (!this.filter) {
      return;
    }
    this.refreshForm = true;
    this.editing = false;
    this.dataLoaded = false;
    this.columns = [];
    this.events = await this.admin.retrieveEvents(
      this.filter,
      this.eventFilter === "None" ? "" : this.eventFilter,
      this.sectionFilter === "None" ? "" : this.sectionFilter,
      this.aggregator,
      this.startDate,
      this.endDate,
      this.limit
    );
    if (this.events && this.events.length) {
      this.dataLoaded = true;

      switch (this.displayType) {
        case "table":
          this.processDataForTable();
          break;
        case "pie-chart":
          this.processDataForPieChart();
          break;
        case "line-chart":
        case "bar-chart":
          this.processDataForLineChart();
          break;
        case "number":
          break;
        case "object":
          break;
      }
      //let event = this.events[0];
      return true;
    } else {
      return false;
    }
  }
  public processDataForLineChart() {
    if (!this.aggregator) {
      alert("Line Chart view requires aggregator.");
    } else {
      this.pieData = [];
      this.pieLabels = [];
      this.events.map((ev) => {
        for (let k of Object.keys(ev)) {
          this.pieData.push(ev[k]);
          this.pieLabels.push(k);
        }
      });
      this.lineChartDataSets = [{ data: this.pieData, label: "Count" }];
    }
  }

  public processDataForPieChart() {
    if (!this.aggregator) {
      alert("Pie Chart view requires aggregator.");
    } else {
      this.pieData = [];
      this.pieLabels = [];
      this.events.map((ev) => {
        for (let k of Object.keys(ev)) {
          this.pieData.push(ev[k]);
          this.pieLabels.push(k);
        }
      });
    }
  }
  public processDataForTable() {
    if (!this.aggregator) {
      let cols = this.filter.match(/EVENT\([a-z_]+/g);
      for (let column of cols) {
        let key = column.replace(/EVENT\(/g, "");
        this.columns.push({
          field: key,
          sortable: true,
          filter: true,
          editable: true,
        });
      }
      this.rows = [];
      this.events.map((ev) => {
        let obj = {};
        for (let i in ev) {
          if (this.columns && this.columns.length) {
            obj[this.columns[i].field] = ev[i];
          }
        }
        this.rows.push(obj);
      });
    } else {
      this.rows = [];
      this.columns = [
        { field: "name", sortable: true, filter: true, editable: true },
        { field: "number", sortable: true, filter: true, editable: true },
      ];
      this.events.map((ev) => {
        let obj = {};
        for (let k of Object.keys(ev)) {
          obj["name"] = k;
          obj["number"] = ev[k];
        }
        this.rows.push(obj);
      });
    }
  }
  public async retrieveSettings() {
    let settings = await this.admin.retrieveDashboardInfo("widgets", this._uid);
    if (settings) {
      if (settings["filter"]) {
        this.filter = settings["filter"];
      }
      if (settings["limit"]) {
        this.limit = settings["limit"];
      }
      if (settings["aggregator"]) {
        this.aggregator = settings["aggregator"];
      }
      if (settings["sectionFilter"]) {
        this.sectionFilter = settings["sectionFilter"];
      }
      if (settings["eventFilter"]) {
        this.eventFilter = settings["eventFilter"];
      }
      if (settings["title"]) {
        this.title = settings["title"];
      }
      if (settings["subtitle"]) {
        this.subtitle = settings["subtitle"];
      }
      if (settings["displayType"]) {
        this.displayType = settings["displayType"];
      }
    }
  }
  public async save() {
    if (this.refresh()) {
      //only save if the query successfully returned data.
      await this.saveSettings();
    }
  }
  public async delete() {
    if (this._uid) {
      let result = await this.admin.deleteDashboardInfo("widgets", this._uid);
      if (result) {
        this.deleted.emit(this._uid);
      }
    }
  }
  public async saveSettings() {
    if (this._uid) {
      //only save widgets with a specified uid.
      let settings = {};
      settings["id"] = this._uid;
      settings["filter"] = this.filter;
      settings["limit"] = this.limit;
      settings["aggregator"] = this.aggregator;
      settings["sectionFilter"] = this.sectionFilter;
      settings["eventFilter"] = this.eventFilter;
      settings["title"] = this.title;
      settings["subtitle"] = this.subtitle;
      settings["displayType"] = this.displayType;

      if (await this.admin.saveDashboardInfo("widgets", settings)) {
        console.log("saved dashboard widget successfully: " + this._uid);
      }
    }
  }
}
